// Story 10.2 — Dashboard principal con grid de Bot Cards, search/sort en URL
// params, EmptyState, error retry, skeleton loading.
// Story 10.5 — escucha `?error=forbidden&capability=...` y dispara toast.
// Story 16.5 — `?org=<id>` filtra por organización cuando hay bots de varias.

import { useNavigate, useSearch } from '@tanstack/react-router';
import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Can } from '@/components/auth/Can';
import { BotCard } from '@/components/bots/BotCard';
import { BotCardSkeleton } from '@/components/bots/BotCardSkeleton';
import { BotCreationWizard } from '@/components/bots/BotCreationWizard';
import { BotsToolbar } from '@/components/bots/BotsToolbar';
import { EmptyState } from '@/components/bots/EmptyState';
import { Button } from '@/components/ui/button';
import { useBots } from '@/hooks/queries/use-bots';
import {
  filterAndSortBots,
  organizationsOf,
  parseDashboardSearch,
  parseOrganizationFilter,
  type SortKey,
} from '@/lib/bots-filter';
import type { Capability } from '@/lib/permissions';
import { forbiddenMessage } from '@/lib/route-guards';

export function DashboardPage(): React.ReactElement {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const { q: queryRaw, sort } = parseDashboardSearch(search);
  const organizationFilter = parseOrganizationFilter(search);
  const query = useDeferredValue(queryRaw); // suaviza filtrado en typings rápidos
  const { data, isLoading, isError, refetch } = useBots();
  // Story 13.1 — wizard para crear bot desde web. Compartido entre el header
  // (cuando hay bots) y el EmptyState (cuando no hay).
  // Soporta `?new=1` desde el TopBar: si llegamos con ese param, abrimos el
  // wizard automaticamente y limpiamos el param para que un refresh no lo
  // reabra.
  const [wizardOpen, setWizardOpen] = useState(search.new === 1 || search.new === '1');
  useEffect(() => {
    if (search.new === 1 || search.new === '1') {
      setWizardOpen(true);
      void navigate({
        to: '/',
        // biome-ignore lint/suspicious/noExplicitAny: TanStack search typing relajado.
        search: ((prev: any) => ({ ...(prev ?? {}), new: undefined })) as any,
        replace: true,
      });
    }
  }, [search.new, navigate]);

  // Story 10.5 — toast `?error=forbidden`. Limpia el param tras mostrar para
  // que un refresh no spamee. Ref guard evita doble-fire en React strict mode.
  const forbiddenError = search.error === 'forbidden';
  const forbiddenCapability =
    typeof search.capability === 'string' ? (search.capability as Capability) : undefined;
  const firedToast = useRef(false);
  useEffect(() => {
    // P-315: reset cuando el query-param se limpió. Sin esto, una segunda
    // ruta forbidden en la misma sesión (e.g. owner intenta editor → /,
    // luego intenta logs → /) sería silenciada porque el ref quedó en
    // `true` desde la primera vez.
    if (!forbiddenError) {
      firedToast.current = false;
      return;
    }
    if (firedToast.current) return;
    firedToast.current = true;
    toast.error(forbiddenMessage(forbiddenCapability));
    void navigate({
      to: '/',
      search:
        // biome-ignore lint/suspicious/noExplicitAny: TanStack search typing relajado.
        ((prev: any) => ({
          ...(prev ?? {}),
          error: undefined,
          capability: undefined,
          // biome-ignore lint/suspicious/noExplicitAny: TanStack search typing relajado.
        })) as any,
      replace: true,
    });
  }, [forbiddenError, forbiddenCapability, navigate]);

  const organizations = useMemo(() => organizationsOf(data ?? []), [data]);
  const filtered = useMemo(() => {
    if (!data) return [];
    const inOrganization =
      organizationFilter === undefined
        ? data
        : data.filter((b) => b.organizationId === organizationFilter);
    return filterAndSortBots(inOrganization, query, sort);
  }, [data, query, sort, organizationFilter]);

  function setOrganization(next: number | undefined): void {
    void navigate({
      to: '/',
      // biome-ignore lint/suspicious/noExplicitAny: TanStack search type relaxed for shared widget.
      search: ((prev: any) => ({ ...(prev ?? {}), org: next })) as any,
      replace: true,
    });
  }

  function setQuery(next: string): void {
    void navigate({
      to: '/',
      // biome-ignore lint/suspicious/noExplicitAny: TanStack search type relaxed for shared widget.
      search: ((prev: any) => ({ ...(prev ?? {}), q: next || undefined })) as any,
      replace: true,
    });
  }
  function setSort(next: SortKey): void {
    void navigate({
      to: '/',
      // biome-ignore lint/suspicious/noExplicitAny: see above
      search: ((prev: any) => ({ ...(prev ?? {}), sort: next })) as any,
      replace: true,
    });
  }

  // Error 5xx / network. 401 ya redirigió por el guard del router.
  if (isError) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/10 p-6">
        <h2 className="text-lg font-semibold text-destructive">No se pudieron cargar los bots</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Algo salió mal del lado del servidor. Prueba a reintentar; si persiste, revisa los logs.
        </p>
        <Button onClick={() => refetch()} className="mt-4" variant="outline">
          Reintentar
        </Button>
      </div>
    );
  }

  // Loading state inicial (no hay data cacheada).
  if (isLoading) {
    return (
      <div className="space-y-6">
        <header>
          <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground">Cargando bots…</p>
        </header>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(min(18rem,100%),1fr))] gap-4">
          {['skeleton-1', 'skeleton-2', 'skeleton-3', 'skeleton-4', 'skeleton-5', 'skeleton-6'].map(
            (id) => (
              <BotCardSkeleton key={id} />
            ),
          )}
        </div>
      </div>
    );
  }

  const bots = data ?? [];

  if (bots.length === 0) {
    return (
      <div className="space-y-6">
        <header>
          <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground">Vista general de tus bots.</p>
        </header>
        <EmptyState />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            {bots.length} {bots.length === 1 ? 'bot' : 'bots'} — {filtered.length} visible
            {filtered.length === 1 ? '' : 's'}
          </p>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          {organizations.length > 1 && (
            <select
              aria-label="Organización"
              className="h-9 min-w-0 flex-1 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:w-64 sm:flex-none"
              value={organizationFilter ?? ''}
              onChange={(e) => setOrganization(e.target.value ? Number(e.target.value) : undefined)}
            >
              <option value="">Todas las organizaciones</option>
              {organizations.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          )}
          <Can capability="edit_bot">
            <button
              type="button"
              onClick={() => setWizardOpen(true)}
              className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              + Crear bot
            </button>
          </Can>
        </div>
      </header>
      <BotCreationWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />

      {/* P-246: la toolbar aparece con ≥6 bots O cuando hay un filtro activo
          (q/sort heredados de URL). Sin esto, si el user borraba un bot
          y bajaba de 6 → 5 con `?q=foo` activo, la toolbar desaparecía
          dejando un "ghost state" — grid vacío sin input para limpiar el
          filtro. */}
      {(bots.length > 5 || queryRaw || sort !== 'activity') && (
        <BotsToolbar query={queryRaw} sort={sort} onQueryChange={setQuery} onSortChange={setSort} />
      )}

      {filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {queryRaw
            ? `No hay bots que coincidan con "${queryRaw}".`
            : 'No hay bots en esta organización.'}
        </p>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(min(18rem,100%),1fr))] gap-4">
          {filtered.map((bot) => (
            <BotCard key={bot.id} bot={bot} />
          ))}
        </div>
      )}
    </div>
  );
}
